import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { ClassCodeMaster } from "../../types/assetTypes";
import { getClassCodeColumns } from "../../const/AssertData";
import {
  getClassCodeById,
  getClassCodeMasterDataList,
  saveClassCode,
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { RootState } from "../../store/Reduxstore";

interface ClassCodeProps {
  setPathname: (path: string) => void;
}

const ClassCode: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<ClassCodeMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [classCodeData, setClassCodeData] = useState<ClassCodeMaster>({
    classCodeId: 0,
    classCode: "",
    classCodeDes: ""
  });

  const columns = useMemo(() => getClassCodeColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditClassCode(rowData.classCodeId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" };
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>)
  );

  const handleEditClassCode = async (classCodeId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getClassCodeById(classCodeId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setClassCodeData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const filters = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        if (col.field) {
          acc[col.field] = {
            value: null,
            matchMode: FilterMatchMode.STARTS_WITH,
          };
        }
        return acc;
      },
      {} as Record<string, { value: any; matchMode: FilterMatchMode }>
    );
  }, [columns]);

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getClassCodeMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setClassCodeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setClassCodeData({
      classCodeId: 0,
      classCode: "",
      classCodeDes: ""
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (classCodeData.classCode === "") {
      CustomMessage("Add the class code", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveClassCode(classCodeData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Assets List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditClassCode(rowData.classCodeId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Class Code"
        visible={openDialog}
        onHide={handleClear}
      // allowedRoles={["Admin", "Assets List Write"]}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          <div className="mb-3">
            <h6>Class Code</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={classCodeData.classCode}
              name="classCode"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <div className="mb-3">
            <h6>Description</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={classCodeData.classCodeDes}
              name="classCodeDes"
              onChange={handleChange}
              sx={{ mt: 0 }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "3px",
            gap: 3
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"
            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Assets List Write"]}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default ClassCode;
